import * as React from 'react';
import * as styles from './users.module.scss';
import { PaginatedUserContext } from '../../../shared/paginated-user-context';
import usePaginatedUsers from '../../../hooks/usePaginatedUsers';
import useSearch from '../../../hooks/useSearch';
import useMeasuredRef from '../../../hooks/useMeasuredRef';
import CircularProgress from '@material-ui/core/CircularProgress';
import UserTable from './table/user-table';
import Pagination from '../pagination/pagination';
import SearchBar from '../../common/search-bar';
import { Redirect } from 'react-router';
import Paths from '../../../models/enums/paths';
import SecondaryButton from '../../common/secondary-button';
import PriorityTwoSuccess from '../../announcement/priority-two-success';
import PriorityTwoFailure from '../../announcement/priority-two-failure';
import useTimedAlerts from '../../../hooks/useTimedAlerts';

const Users = () => {
  const { pageSize, currentPage, orderColumn, orderDirection } = React.useContext(PaginatedUserContext);
  const { loading, results, totalResults, fetchUsers } = usePaginatedUsers();
  const { searchQuery: searchTerm, updateSearchQuery, setIsSearching } = useSearch();
  const announcementCount: number = 0; //Announcements are not shown on management UI, so there is no need to get the real count
  const [redirectToAddUser, setRedirectToAddUser] = React.useState(false);
  const { successMessage, setSuccessMessage, errorMessage, setErrorMessage } = useTimedAlerts();
  const { measuredRef, dimensions } = useMeasuredRef([results, announcementCount, successMessage, errorMessage], true);

  // Fetches the user information for the page on mount
  React.useEffect(() => {
    fetchUsers({ currentPage, pageSize, searchTerm, orderColumn, orderDirection });
  }, []);

  if (redirectToAddUser) {
    return <Redirect push to={{ pathname: Paths.AddUser }} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h1>Users</h1>
          <SecondaryButton text="Add a User" title="Add a User" handleClick={() => setRedirectToAddUser(true)} />
        </div>
        <SearchBar
          value={searchTerm}
          updateValue={(val: string) => updateSearchQuery(val)}
          setIsSearching={(searching: boolean) => setIsSearching(searching)}
          placeholder={'Search Users'}
          submitHandler={() =>
            fetchUsers({
              currentPage: 1,
              pageSize: pageSize,
              searchTerm,
              orderColumn,
              orderDirection
            })
          }
        />
      </div>
      {errorMessage && <PriorityTwoFailure message={errorMessage} />}
      {successMessage && <PriorityTwoSuccess message={successMessage} />}
      {loading && results.length === 0 ? (
        <div className={styles.circularProgress}>
          <CircularProgress />
        </div>
      ) : !results || results.length === 0 ? (
        <span>There are no users.</span>
      ) : (
        <div
          className={styles.center}
          ref={measuredRef}
          style={{ height: `calc(100vh - ${dimensions ? 20 + dimensions.top : 0}px` }}
        >
          {(loading && results.length > 0) || (loading && results.length === 0) ? (
            <div className={styles.circularProgress}>
              <CircularProgress />
            </div>
          ) : null}
          <div className={styles.content}>
            <div>
              <span className={styles.resultsCount}>{totalResults} results</span>
            </div>
            {<UserTable loading={loading} setErrorMessage={setErrorMessage} setSuccessMessage={setSuccessMessage} />}
          </div>
          <Pagination fetchResults={fetchUsers} paginatedContext={PaginatedUserContext} searchTerm={searchTerm} />
        </div>
      )}
    </div>
  );
};

export default Users;
